import SettingsNavigation, { type SettingsNavigationGroup } from './settings/SettingsNavigation';
import GeneralIcon from './icons/settings/GeneralIcon';
import IMBotIcon from './icons/settings/IMBotIcon';
import EmailIcon from './icons/settings/EmailIcon';
import MemoryIcon from './icons/settings/MemoryIcon';
import ShortcutsIcon from './icons/settings/ShortcutsIcon';
import MemoryEntriesPanel from './cowork/MemoryEntriesPanel';
import MemoryFilesPanel from './cowork/MemoryFilesPanel';
import MemoryFileEditor from './cowork/MemoryFileEditor';
import SecretaryPreferences from './cowork/SecretaryPreferences';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { configService } from '../services/config';
import { themeService, ACCENT_COLORS, type AccentColorType } from '../services/theme';
import { i18nService, LanguageType } from '../services/i18n';
import { coworkService } from '../services/cowork';
import type { AppUpdateInfo } from '../services/appUpdate';
import ErrorMessage from './ErrorMessage';
import SidebarToggleIcon from './icons/SidebarToggleIcon';
import ComposeIcon from './icons/ComposeIcon';
import WindowTitleBar from './window/WindowTitleBar';
import { XMarkIcon, EyeIcon, EyeSlashIcon, PaintBrushIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import type {
  CoworkExecutionMode,
} from '../types/cowork';
import IMInstancesPanel from './im/IMInstancesPanel';
import EmailSkillConfig from './skills/EmailSkillConfig';
import { isWebMode } from '../platform';
import type { AuthState } from '../types/auth';
import CreditUsageCard from './account/CreditUsageCard';

type TabType = 'appearance' | 'general' | 'account' | 'coworkSandbox' | 'coworkMemory' | 'shortcuts' | 'im' | 'email' | 'toolIntegration';

const normalizeSettingsTab = (tab?: TabType): TabType =>
  tab === 'coworkSandbox' || tab === 'toolIntegration' ? 'account' : (tab ?? 'general');

export type SettingsOpenOptions = {
  initialTab?: TabType;
  notice?: string;
};

interface SettingsProps extends SettingsOpenOptions {
  onClose: () => void;
  embedded?: boolean;
  authState?: AuthState | null;
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  onNewChat?: () => void;
  onLogout?: () => Promise<void>;
  onShowToast?: (message: string) => void;
  onCheckUpdate?: () => Promise<void>;
  updateInfo?: AppUpdateInfo | null;
}


const getAccountStatusLabel = (status: number | null | undefined): string | null => {
  if (status == null) {
    return null;
  }
  if (status === 1) {
    return i18nService.t('authUserStatusNormal');
  }
  return i18nService.t('authUserStatusUnknown');
};

const Settings: React.FC<SettingsProps> = ({ onClose, initialTab, notice, embedded, authState, isSidebarCollapsed, onToggleSidebar, onNewChat, onLogout, onShowToast }) => {
  const isMac = window.electron.platform === 'darwin';
  // 状态
  const [activeTab, setActiveTab] = useState<TabType>(() => normalizeSettingsTab(initialTab));
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [accentColor, setAccentColor] = useState<AccentColorType>('blue');
  const [language, setLanguage] = useState<LanguageType>('zh');
  const [autoLaunch, setAutoLaunchState] = useState(false);
  const [isUpdatingAutoLaunch, setIsUpdatingAutoLaunch] = useState(false);
  const [useSystemProxy, setUseSystemProxy] = useState(false);
  const [quickActionSuggestionsEnabled, setQuickActionSuggestionsEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(notice ?? null);
  const initialThemeRef = useRef<'light' | 'dark' | 'system'>(themeService.getTheme());
  const initialAccentRef = useRef<AccentColorType>(themeService.getAccentColor());
  const initialLanguageRef = useRef<LanguageType>(i18nService.getLanguage());
  const didSaveRef = useRef(false);

  // 创建引用来确保内容区域的滚动
  const contentRef = useRef<HTMLDivElement>(null);

  // 快捷键设置
  const [shortcuts, setShortcuts] = useState({
    newChat: 'Ctrl+N',
    search: 'Ctrl+F',
    settings: 'Ctrl+,',
  });


  const coworkConfig = useSelector((state: RootState) => state.cowork.config);

  const [coworkExecutionMode, setCoworkExecutionMode] = useState<CoworkExecutionMode>(coworkConfig.executionMode || 'sandbox');
  const [coworkMemoryEnabled, setCoworkMemoryEnabled] = useState<boolean>(coworkConfig.memoryEnabled ?? true);
  const [coworkMemoryImplicitEnabled, setCoworkMemoryImplicitEnabled] = useState<boolean>(coworkConfig.memoryImplicitUpdateEnabled ?? true);
  // ── BoMate 长期记忆 + Soul ──
  const [soulFileOpen, setSoulFileOpen] = useState(false);
  const [secretarySoulOpen, setSecretarySoulOpen] = useState(false);

  useEffect(() => {
    setCoworkExecutionMode(coworkConfig.executionMode || 'sandbox');
    setCoworkMemoryEnabled(coworkConfig.memoryEnabled ?? true);
    setCoworkMemoryImplicitEnabled(coworkConfig.memoryImplicitUpdateEnabled ?? true);
  }, [
    coworkConfig.executionMode,
    coworkConfig.memoryEnabled,
    coworkConfig.memoryImplicitUpdateEnabled,
  ]);

  useEffect(() => {
    try {
      const config = configService.getConfig();
      
      // Set general settings
      initialThemeRef.current = config.theme;
      initialAccentRef.current = config.accentColor || 'blue';
      initialLanguageRef.current = config.language;
      setTheme(config.theme);
      setAccentColor(config.accentColor || 'blue');
      setLanguage(config.language);

      // Load auto-launch setting
      window.electron.autoLaunch.get().then(({ enabled }) => {
        setAutoLaunchState(enabled);
      }).catch(err => {
        console.error('Failed to load auto-launch setting:', err);
      });

      // Load system proxy setting
      setUseSystemProxy(config.useSystemProxy === true);

      // Load quick action suggestions setting
      setQuickActionSuggestionsEnabled(config.quickActionSuggestionsEnabled === true);

      // 加载快捷键设置
      if (config.shortcuts) {
        setShortcuts(prev => ({
          ...prev,
          ...config.shortcuts,
        }));
      }
    } catch (error) {
      setError('Failed to load settings');
    }
  }, []);

  useEffect(() => {
    return () => {
      if (didSaveRef.current) {
        return;
      }
      themeService.setTheme(initialThemeRef.current);
      themeService.setAccentColor(initialAccentRef.current);
      i18nService.setLanguage(initialLanguageRef.current, { persist: false });
    };
  }, []);

  // 监听标签页切换，确保内容区域滚动到顶部
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    setNoticeMessage(notice ?? null);
  }, [notice]);

  // Clear login notice when user logs in
  useEffect(() => {
    if (authState && noticeMessage) {
      setNoticeMessage(null);
    }
  }, [authState]);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(normalizeSettingsTab(initialTab));
    }
  }, [initialTab]);


  // Subscribe to language changes
  useEffect(() => {
    const unsubscribe = i18nService.subscribe(() => {
      setLanguage(i18nService.getLanguage());
    });
    return unsubscribe;
  }, []);

  const hasCoworkConfigChanges = coworkExecutionMode !== coworkConfig.executionMode
    || coworkMemoryEnabled !== coworkConfig.memoryEnabled
    || coworkMemoryImplicitEnabled !== coworkConfig.memoryImplicitUpdateEnabled;

  /** 保存可编辑的设置；账户积分和支付均有独立操作，不能触发通用设置写入。 */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (activeTab === 'account' || activeTab === 'im') return;
    setError(null);

    try {
      await configService.updateConfig({
        theme,
        accentColor,
        language,
        shortcuts,
        useSystemProxy,
        quickActionSuggestionsEnabled,
      });

      // 应用主题
      themeService.setTheme(theme);
      themeService.setAccentColor(accentColor);

      // 应用语言
      i18nService.setLanguage(language, { persist: false });

      if (hasCoworkConfigChanges) {
        await coworkService.updateConfig({
          executionMode: coworkExecutionMode,
          memoryEnabled: coworkMemoryEnabled,
          memoryImplicitUpdateEnabled: coworkMemoryImplicitEnabled,
        });
      }

      didSaveRef.current = true;
      onShowToast?.(i18nService.t('settingsSaveSuccess'));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save settings');
    }
  };

  // 标签页切换处理
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  // 快捷键更新处理
  const handleShortcutChange = (key: keyof typeof shortcuts, value: string) => {
    setShortcuts(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const [showPhone, setShowPhone] = useState(false);

  const handleLogoutClick = async () => {
    if (!onLogout) return;
    try {
      await onLogout();
    } catch (logoutError) {
      onShowToast?.(logoutError instanceof Error ? logoutError.message : i18nService.t('connectionFailed'));
    }
  };

  // 阻止点击设置窗口时事件传播到背景
  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  /** 按客户端的信息结构分组，只列出当前运行环境支持的设置。 */
  const sidebarGroups = useMemo<SettingsNavigationGroup<TabType>[]>(() => [
    { key: 'basic', label: i18nService.t('settingsBasicGroup'), items: [
      { key: 'general', label: i18nService.t('general'), icon: <GeneralIcon className="h-5 w-5" /> },
      { key: 'appearance', label: i18nService.t('appearance'), icon: <PaintBrushIcon className="h-5 w-5" /> },
      { key: 'account', label: i18nService.t('authAccount'), icon: <UserCircleIcon className="h-5 w-5" /> },
    ] },
    { key: 'capabilities', label: i18nService.t('settingsCapabilityGroup'), items: [
      { key: 'im', label: i18nService.t('imBot'), icon: <IMBotIcon className="h-5 w-5" /> },
      { key: 'email', label: i18nService.t('emailTab'), icon: <EmailIcon className="h-5 w-5" /> },
      { key: 'coworkMemory', label: i18nService.t('coworkMemoryTitle'), icon: <MemoryIcon className="h-5 w-5" /> },
    ] },
    { key: 'system', label: i18nService.t('settingsSystemGroup'), items: isWebMode() ? [] : [
      { key: 'shortcuts', label: i18nService.t('shortcuts'), icon: <ShortcutsIcon className="h-5 w-5" /> },
    ] },
  ], [language]);
  const activeTabLabel = sidebarGroups.flatMap(group => group.items).find(item => item.key === activeTab)?.label;

  const renderTabContent = () => {
    switch(activeTab) {
      case 'general':
      case 'appearance':
        return (
          <div className="space-y-8">
            {activeTab === 'general' && <>
            {/* Language Section */}
            <div>
              <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text mb-3">
                {i18nService.t('language')}
              </h4>
              <select
                aria-label={i18nService.t('language')}
                value={language}
                onChange={event => {
                  const next = event.target.value as LanguageType;
                  setLanguage(next);
                  i18nService.setLanguage(next, { persist: false });
                }}
                className="w-full max-w-xs rounded-xl border border-claude-border bg-claude-surface px-3 py-2.5 text-sm text-claude-text focus:border-claude-accent focus:outline-none dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText"
              >
                <option value="zh">{i18nService.t('chinese')}</option>
                <option value="zh-TW">{i18nService.t('traditionalChinese')}</option>
                <option value="en">{i18nService.t('english')}</option>
              </select>
            </div>

            {/* Auto-launch Section — Electron only */}
            {!isWebMode() && (
            <div>
              <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text mb-3">
                {i18nService.t('autoLaunch')}
              </h4>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm dark:text-claude-darkSecondaryText text-claude-secondaryText">
                  {i18nService.t('autoLaunchDescription')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoLaunch}
                  onClick={async () => {
                    if (isUpdatingAutoLaunch) return;
                    const next = !autoLaunch;
                    setIsUpdatingAutoLaunch(true);
                    try {
                      const result = await window.electron.autoLaunch.set(next);
                      if (result.success) {
                        setAutoLaunchState(next);
                      } else {
                        setError(result.error || 'Failed to update auto-launch setting');
                      }
                    } catch (err) {
                      console.error('Failed to set auto-launch:', err);
                      setError('Failed to update auto-launch setting');
                    } finally {
                      setIsUpdatingAutoLaunch(false);
                    }
                  }}
                  disabled={isUpdatingAutoLaunch}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    isUpdatingAutoLaunch ? 'opacity-50 cursor-not-allowed' : ''
                  } ${
                    autoLaunch
                      ? 'bg-claude-accent'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoLaunch ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>
            )}

            {/* System Proxy Section — Electron only */}
            {!isWebMode() && (
            <div>
              <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text mb-3">
                {i18nService.t('useSystemProxy')}
              </h4>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm dark:text-claude-darkSecondaryText text-claude-secondaryText">
                  {i18nService.t('useSystemProxyDescription')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useSystemProxy}
                  onClick={() => setUseSystemProxy(prev => !prev)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    useSystemProxy
                      ? 'bg-claude-accent'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useSystemProxy ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>
            )}

            {/* Quick Action Suggestions Section */}
            <div>
              <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text mb-3">
                {i18nService.t('quickActionSuggestions')}
              </h4>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm dark:text-claude-darkSecondaryText text-claude-secondaryText">
                  {i18nService.t('quickActionSuggestionsDescription')}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={quickActionSuggestionsEnabled}
                  onClick={() => setQuickActionSuggestionsEnabled(prev => !prev)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    quickActionSuggestionsEnabled
                      ? 'bg-claude-accent'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      quickActionSuggestionsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>

            </>}
            {activeTab === 'appearance' && <>
            {/* Appearance Section */}
            <div>
              <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text mb-3">
                {i18nService.t('appearance')}
              </h4>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {([
                  { value: 'light' as const, label: i18nService.t('light') },
                  { value: 'dark' as const, label: i18nService.t('dark') },
                  { value: 'system' as const, label: i18nService.t('system') },
                ]).map((option) => {
                  const isSelected = theme === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setTheme(option.value);
                        themeService.setTheme(option.value);
                      }}
                      className={`flex flex-col items-center rounded-xl border-2 p-3 transition-colors cursor-pointer ${
                        isSelected
                          ? 'border-claude-accent bg-claude-accent/5 dark:bg-claude-accent/10'
                          : 'dark:border-claude-darkBorder border-claude-border hover:border-claude-accent/50 dark:hover:border-claude-accent/50'
                      }`}
                    >
                      <svg viewBox="0 0 120 80" className="w-full h-auto rounded-md mb-2 overflow-hidden" xmlns="http://www.w3.org/2000/svg">
                        {option.value === 'light' && (
                          <>
                            <rect width="120" height="80" fill="#F8F9FB" />
                            <rect x="0" y="0" width="30" height="80" fill="#EBEDF0" />
                            <rect x="4" y="8" width="22" height="4" rx="2" fill="#C8CBD0" />
                            <rect x="4" y="16" width="18" height="3" rx="1.5" fill="#D5D7DB" />
                            <rect x="4" y="22" width="20" height="3" rx="1.5" fill="#D5D7DB" />
                            <rect x="4" y="28" width="16" height="3" rx="1.5" fill="#D5D7DB" />
                            <rect x="36" y="8" width="78" height="64" rx="4" fill="#FFFFFF" />
                            <rect x="42" y="16" width="50" height="4" rx="2" fill="#D5D7DB" />
                            <rect x="42" y="24" width="66" height="3" rx="1.5" fill="#E2E4E7" />
                            <rect x="42" y="30" width="60" height="3" rx="1.5" fill="#E2E4E7" />
                            <rect x="42" y="36" width="55" height="3" rx="1.5" fill="#E2E4E7" />
                            <rect x="42" y="46" width="40" height="4" rx="2" fill="#D5D7DB" />
                            <rect x="42" y="54" width="66" height="3" rx="1.5" fill="#E2E4E7" />
                            <rect x="42" y="60" width="58" height="3" rx="1.5" fill="#E2E4E7" />
                          </>
                        )}
                        {option.value === 'dark' && (
                          <>
                            <rect width="120" height="80" fill="#0F1117" />
                            <rect x="0" y="0" width="30" height="80" fill="#151820" />
                            <rect x="4" y="8" width="22" height="4" rx="2" fill="#3A3F4B" />
                            <rect x="4" y="16" width="18" height="3" rx="1.5" fill="#2A2F3A" />
                            <rect x="4" y="22" width="20" height="3" rx="1.5" fill="#2A2F3A" />
                            <rect x="4" y="28" width="16" height="3" rx="1.5" fill="#2A2F3A" />
                            <rect x="36" y="8" width="78" height="64" rx="4" fill="#1A1D27" />
                            <rect x="42" y="16" width="50" height="4" rx="2" fill="#3A3F4B" />
                            <rect x="42" y="24" width="66" height="3" rx="1.5" fill="#252930" />
                            <rect x="42" y="30" width="60" height="3" rx="1.5" fill="#252930" />
                            <rect x="42" y="36" width="55" height="3" rx="1.5" fill="#252930" />
                            <rect x="42" y="46" width="40" height="4" rx="2" fill="#3A3F4B" />
                            <rect x="42" y="54" width="66" height="3" rx="1.5" fill="#252930" />
                            <rect x="42" y="60" width="58" height="3" rx="1.5" fill="#252930" />
                          </>
                        )}
                        {option.value === 'system' && (
                          <>
                            <defs>
                              <clipPath id="left-half">
                                <rect x="0" y="0" width="60" height="80" />
                              </clipPath>
                              <clipPath id="right-half">
                                <rect x="60" y="0" width="60" height="80" />
                              </clipPath>
                            </defs>
                            {/* Light half */}
                            <g clipPath="url(#left-half)">
                              <rect width="120" height="80" fill="#F8F9FB" />
                              <rect x="0" y="0" width="30" height="80" fill="#EBEDF0" />
                              <rect x="4" y="8" width="22" height="4" rx="2" fill="#C8CBD0" />
                              <rect x="4" y="16" width="18" height="3" rx="1.5" fill="#D5D7DB" />
                              <rect x="4" y="22" width="20" height="3" rx="1.5" fill="#D5D7DB" />
                              <rect x="4" y="28" width="16" height="3" rx="1.5" fill="#D5D7DB" />
                              <rect x="36" y="8" width="78" height="64" rx="4" fill="#FFFFFF" />
                              <rect x="42" y="16" width="50" height="4" rx="2" fill="#D5D7DB" />
                              <rect x="42" y="24" width="66" height="3" rx="1.5" fill="#E2E4E7" />
                              <rect x="42" y="30" width="60" height="3" rx="1.5" fill="#E2E4E7" />
                              <rect x="42" y="36" width="55" height="3" rx="1.5" fill="#E2E4E7" />
                              <rect x="42" y="46" width="40" height="4" rx="2" fill="#D5D7DB" />
                              <rect x="42" y="54" width="66" height="3" rx="1.5" fill="#E2E4E7" />
                            </g>
                            {/* Dark half */}
                            <g clipPath="url(#right-half)">
                              <rect width="120" height="80" fill="#0F1117" />
                              <rect x="0" y="0" width="30" height="80" fill="#151820" />
                              <rect x="4" y="8" width="22" height="4" rx="2" fill="#3A3F4B" />
                              <rect x="4" y="16" width="18" height="3" rx="1.5" fill="#2A2F3A" />
                              <rect x="4" y="22" width="20" height="3" rx="1.5" fill="#2A2F3A" />
                              <rect x="4" y="28" width="16" height="3" rx="1.5" fill="#2A2F3A" />
                              <rect x="36" y="8" width="78" height="64" rx="4" fill="#1A1D27" />
                              <rect x="42" y="16" width="50" height="4" rx="2" fill="#3A3F4B" />
                              <rect x="42" y="24" width="66" height="3" rx="1.5" fill="#252930" />
                              <rect x="42" y="30" width="60" height="3" rx="1.5" fill="#252930" />
                              <rect x="42" y="36" width="55" height="3" rx="1.5" fill="#252930" />
                              <rect x="42" y="46" width="40" height="4" rx="2" fill="#3A3F4B" />
                              <rect x="42" y="54" width="66" height="3" rx="1.5" fill="#252930" />
                            </g>
                            {/* Divider line */}
                            <line x1="60" y1="0" x2="60" y2="80" stroke="#888" strokeWidth="0.5" />
                          </>
                        )}
                      </svg>
                      <span className={`text-xs font-medium ${
                        isSelected
                          ? 'text-claude-accent'
                          : 'dark:text-claude-darkText text-claude-text'
                      }`}>
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Accent Color Section */}
            <div>
              <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text mb-3">
                {i18nService.t('accentColor')}
              </h4>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(Object.keys(ACCENT_COLORS) as AccentColorType[]).map((colorKey) => {
                  const color = ACCENT_COLORS[colorKey];
                  const isSelected = accentColor === colorKey;
                  const currentLang = i18nService.getLanguage();
                  const label = currentLang === 'zh' ? color.zhName : currentLang === 'zh-TW' ? color.zhTwName : color.enName;
                  return (
                    <button
                      key={colorKey}
                      type="button"
                      onClick={() => {
                        setAccentColor(colorKey);
                        themeService.setAccentColor(colorKey);
                      }}
                      className={`flex flex-col items-center rounded-xl border-2 p-2.5 transition-colors cursor-pointer ${
                        isSelected
                          ? 'border-current bg-opacity-5'
                          : 'dark:border-claude-darkBorder border-claude-border hover:border-opacity-50'
                      }`}
                      style={isSelected ? { borderColor: color.main, backgroundColor: `${color.main}10` } : undefined}
                    >
                      <div
                        className="w-8 h-8 rounded-full mb-1.5 relative"
                        style={{ background: `linear-gradient(135deg, ${color.main}, ${color.hover})` }}
                      >
                        {isSelected && (
                          <svg className="absolute inset-0 m-auto w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className={`text-xs font-medium ${
                        isSelected
                          ? ''
                          : 'dark:text-claude-darkText text-claude-text'
                      }`}
                        style={isSelected ? { color: color.main } : undefined}
                      >
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            </>}
          </div>
        );

      case 'email':
        return <EmailSkillConfig />;


      case 'coworkMemory':
        return (
          <div className="space-y-6">
            {/* Toggle switches section */}
            <div className="space-y-0 rounded-xl border dark:border-claude-darkBorder border-claude-border overflow-hidden">
              <label className="flex items-center justify-between gap-4 px-4 py-3.5 cursor-pointer hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium dark:text-claude-darkText text-claude-text">
                    {i18nService.t('coworkMemoryEnabled')}
                  </div>
                  <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-0.5">
                    {i18nService.t('coworkMemoryEnabledHint')}
                  </div>
                </div>
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={coworkMemoryEnabled}
                    onChange={(event) => setCoworkMemoryEnabled(event.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 rounded-full peer-checked:bg-claude-accent transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
                </div>
              </label>
              <label className={`flex items-center justify-between gap-4 px-4 py-3.5 ${coworkMemoryEnabled ? 'cursor-pointer hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover' : 'opacity-50 cursor-not-allowed'} transition-colors border-t dark:border-claude-darkBorder border-claude-border`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium dark:text-claude-darkText text-claude-text">
                    {i18nService.t('coworkMemoryCaptureEachTurn')}
                  </div>
                  <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-0.5">
                    {i18nService.t('coworkMemoryCaptureEachTurnHint')}
                  </div>
                </div>
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={coworkMemoryImplicitEnabled}
                    onChange={(event) => setCoworkMemoryImplicitEnabled(event.target.checked)}
                    disabled={!coworkMemoryEnabled}
                    className="sr-only peer"
                  />
                  <div className={`w-9 h-5 rounded-full transition-colors ${coworkMemoryImplicitEnabled && coworkMemoryEnabled ? 'bg-claude-accent' : 'bg-gray-300 dark:bg-gray-600'}`} />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
                </div>
              </label>
            </div>

            <MemoryEntriesPanel key={`entries:${authState?.user.id}:${authState?.loggedInAt}`} />

            {/* ── BoMate 长期记忆（memdir 文件仓 + 读模型）── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text">
                    {i18nService.t('secretaryMemoryTitle')}
                  </h4>

                </div>
                <button
                  type="button"
                  onClick={() => setSecretarySoulOpen((v) => !v)}
                  className="px-3 py-1.5 text-xs rounded-lg border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
                >
                  {i18nService.t('secretarySoulEdit')}
                </button>
              </div>

              {secretarySoulOpen && <SecretaryPreferences key={`${authState?.user.id}:${authState?.loggedInAt}`} />}
              {secretarySoulOpen && (
                <div className="mb-4 rounded-xl border dark:border-claude-darkBorder border-claude-border p-3 space-y-2">
                  <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    {i18nService.t('secretarySoulHint')}
                  </div>
                  <button type="button" onClick={() => setSoulFileOpen(true)}
                    className="px-3 py-1.5 text-sm rounded-lg bg-claude-accent text-white">
                    {i18nService.t('memoryFile_soulEditHistory')}
                  </button>
                  {soulFileOpen && <MemoryFileEditor key={`soul:${authState?.user.id}:${authState?.loggedInAt}`} id="SOUL" scope="soul"
                    onClose={() => setSoulFileOpen(false)} onSaved={() => onShowToast?.(i18nService.t('secretarySoulSavedToast'))} />}
                </div>
              )}

              <MemoryFilesPanel key={`files:${authState?.user.id}:${authState?.loggedInAt}`} />

            </div>

          </div>
        );

      case 'account':
        return (
          <div className="space-y-8">
            <div>
              <h4 className="mb-3 text-sm font-medium dark:text-claude-darkText text-claude-text">
                {i18nService.t('authAccountStatus')}
              </h4>
              {authState ? (
                (() => {
                  const accountLabel = authState.user.displayName || authState.user.username;
                  const accountInitial = accountLabel.slice(0, 1).toUpperCase();
                  const statusLabel = getAccountStatusLabel(authState.user.status);
                  return (
                <div className="flex items-center gap-4 rounded-2xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface px-4 py-4">
                  {authState.user.avatarUrl ? (
                    <img
                      src={authState.user.avatarUrl}
                      alt={accountLabel}
                      className="h-14 w-14 shrink-0 rounded-2xl object-cover"
                    />
                  ) : (
                    <div
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl font-bold text-white shadow-sm"
                      style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent-color, #3B82F6) 70%, white) 0%, var(--accent-color, #3B82F6) 100%)' }}
                    >
                      {accountInitial}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-medium dark:text-claude-darkText text-claude-text break-words">
                      {accountLabel}
                    </div>
                    {authState.user.email && authState.user.email !== accountLabel && (
                      <div className="mt-1 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary break-words">
                        {authState.user.email}
                      </div>
                    )}
                    {authState.user.phoneNumber && (
                      <div className="mt-1 flex items-center gap-1.5 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                        <span>{showPhone ? authState.user.phoneNumber : authState.user.phoneNumber.replace(/^(\d{3})\d{4}(\d+)$/, '$1****$2')}</span>
                        <button
                          type="button"
                          onClick={() => setShowPhone(!showPhone)}
                          className="p-0.5 rounded hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
                        >
                          {showPhone
                            ? <EyeSlashIcon className="h-3.5 w-3.5" />
                            : <EyeIcon className="h-3.5 w-3.5" />
                          }
                        </button>
                      </div>
                    )}
                    {statusLabel && (
                      <div className="mt-1 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                        {i18nService.t('authUserStatus')}: {statusLabel}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { void handleLogoutClick(); }}
                    className="shrink-0 self-start rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-900/20"
                  >
                    {i18nService.t('logout')}
                  </button>
                </div>
                  );
                })()
              ) : (
                <div className="rounded-2xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface px-4 py-4">
                  <div className="text-sm dark:text-claude-darkText text-claude-text">
                    {i18nService.t('authNotLoggedIn')}
                  </div>
                </div>
              )}
            </div>
            {authState && <CreditUsageCard key={`${authState.user.id}:${authState.loggedInAt}`} />}
          </div>
        );

      case 'shortcuts':
        return (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-3">
                {i18nService.t('keyboardShortcuts')}
              </label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm dark:text-claude-darkText text-claude-text">{i18nService.t('newChat')}</span>
                  <input
                    type="text"
                    value={shortcuts.newChat}
                    onChange={(e) => handleShortcutChange('newChat', e.target.value)}
                    data-shortcut-input="true"
                    className="w-32 rounded-xl bg-claude-surfaceInset dark:bg-claude-darkSurfaceInset dark:border-claude-darkBorder border-claude-border border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-1.5 text-sm"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm dark:text-claude-darkText text-claude-text">{i18nService.t('search')}</span>
                  <input
                    type="text"
                    value={shortcuts.search}
                    onChange={(e) => handleShortcutChange('search', e.target.value)}
                    data-shortcut-input="true"
                    className="w-32 rounded-xl bg-claude-surfaceInset dark:bg-claude-darkSurfaceInset dark:border-claude-darkBorder border-claude-border border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-1.5 text-sm"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm dark:text-claude-darkText text-claude-text">{i18nService.t('openSettings')}</span>
                  <input
                    type="text"
                    value={shortcuts.settings}
                    onChange={(e) => handleShortcutChange('settings', e.target.value)}
                    data-shortcut-input="true"
                    className="w-32 rounded-xl bg-claude-surfaceInset dark:bg-claude-darkSurfaceInset dark:border-claude-darkBorder border-claude-border border focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30 dark:text-claude-darkText text-claude-text px-3 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'im':
        return <IMInstancesPanel />;

      default:
        return null;
    }
  };

  /** 弹窗与嵌入页面共用操作区；账户页只关闭，不出现含义不明的“保存积分”。 */
  const renderFooter = () => (
    <div className="flex shrink-0 justify-end space-x-4 border-t border-claude-border p-4 dark:border-claude-darkBorder dark:bg-claude-darkBg bg-claude-bg">
      <button type="button" onClick={onClose}
        className="px-4 py-2 dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover rounded-xl transition-colors text-sm font-medium border dark:border-claude-darkBorder border-claude-border">
        {i18nService.t((activeTab === 'account' || activeTab === 'im') ? 'close' : 'cancel')}
      </button>
      {activeTab !== 'account' && activeTab !== 'im' && <button type="submit"
        className="px-4 py-2 bg-claude-accent hover:bg-claude-accentHover text-white rounded-xl text-sm font-medium">
        {i18nService.t('save')}
      </button>}
    </div>
  );

  /** 页面壳统一滚动与保存边界，切换导航不重建设置草稿。 */
  const renderWorkspace = () => (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col sm:flex-row">
      <SettingsNavigation groups={sidebarGroups} active={activeTab} label={i18nService.t('settings')} onSelect={handleTabChange} />
      <form onSubmit={handleSubmit} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="mx-auto w-full max-w-4xl space-y-6 px-5 py-6 sm:px-8">
            <h2 className="text-xl font-semibold text-claude-text dark:text-claude-darkText">{activeTabLabel}</h2>
            {noticeMessage && <ErrorMessage message={noticeMessage} onClose={() => setNoticeMessage(null)} />}
            {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
            {renderTabContent()}
          </div>
        </div>
        {renderFooter()}
      </form>
    </div>
  );

  if (embedded) {
    return (
      <div className="min-w-0 flex-1 flex flex-col dark:bg-claude-darkBg bg-claude-bg h-full">
        {/* Header */}
        <div className="draggable flex h-12 items-center justify-between px-4 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
          <div className="flex items-center space-x-3 h-8">
            {isSidebarCollapsed && (
              <div className={`non-draggable flex items-center gap-1 ${isMac ? 'pl-[68px]' : ''}`}>
                <button
                  type="button"
                  onClick={onToggleSidebar}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
                >
                  <SidebarToggleIcon className="h-4 w-4" isCollapsed={true} />
                </button>
                <button
                  type="button"
                  onClick={onNewChat}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
                >
                  <ComposeIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            <h1 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
              {i18nService.t('appSettings')}
            </h1>
          </div>
          <WindowTitleBar inline />
        </div>

        {renderWorkspace()}

      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div
        className="flex h-[85vh] w-[95vw] flex-col overflow-hidden rounded-2xl border border-claude-border shadow-modal dark:border-claude-darkBorder dark:bg-claude-darkBg bg-claude-bg md:h-[80vh] md:w-[min(1120px,95vw)]"
        onClick={handleSettingsClick}
      >
        {/* Header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-claude-border px-5 dark:border-claude-darkBorder md:px-6">
          <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
            {i18nService.t('appSettings')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 transition-colors dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:hover:text-claude-darkText hover:text-claude-text"
            aria-label={i18nService.t('close')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {renderWorkspace()}
      </div>
    </div>
  );
};

export default Settings; 
